import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { fmtNum, fmtDateTime } from "../lib/format";
import { ArrowsClockwise, Eye, CheckCircle, WarningCircle, ArrowUUpLeft } from "@phosphor-icons/react";

const STATUS_STYLE = {
  closed: { bg: "#D1FAE5", fg: "#065F46" },
  partially_shipped: { bg: "#FEF3C7", fg: "#92400E" },
  open: { bg: "#EFF6FF", fg: "#1D4ED8" },
};

function Stat({ label, value, accent }) {
  return (
    <div data-testid={`recon-stat-${label.toLowerCase().replace(/\s/g, "-")}`}
      style={{ background: "white", border: "1px solid var(--omh-border)", borderLeft: `4px solid ${accent}`,
               borderRadius: 6, padding: "12px 16px", minWidth: 130 }}>
      <div className="muted" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, marginTop: 2, fontFamily: "Inter, sans-serif" }}>{value}</div>
    </div>
  );
}

export default function Reconcile() {
  const [last, setLast] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // 'preview' | 'apply'

  useEffect(() => {
    api.get("/reconcile/erp/status").then((r) => setLast(r.data)).catch(() => {});
  }, []);

  const run = async (dryRun) => {
    setBusy(true);
    setMode(dryRun ? "preview" : "apply");
    try {
      const r = await api.post(`/reconcile/erp/run?dry_run=${dryRun}`);
      setResult(r.data);
      if (!dryRun) {
        setLast(r.data);
        toast.success(`Reconciled — ${r.data.updated} PO(s) updated (${r.data.closed} closed)`);
      } else {
        toast.success(`Preview ready — ${r.data.updated} PO(s) would change`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reconcile failed");
    } finally {
      setBusy(false);
    }
  };

  const view = result || last;

  return (
    <Layout breadcrumb={["Home", "Setup", "ERP Reconcile"]}>
      <div className="jde-page" style={{ maxWidth: 1100 }}>
        <div className="jde-panel" style={{ marginBottom: 16 }}>
          <div className="jde-panel-header">
            <div className="jde-panel-title"><ArrowsClockwise size={15} weight="bold" /> ERP Invoice Reconciliation</div>
          </div>
          <div className="jde-panel-body">
            <p className="muted" style={{ fontSize: 13, marginTop: 0, lineHeight: 1.55 }}>
              Closes open POs against what has actually been <strong>invoiced</strong> in the live portal ERP.
              A PO line counts as shipped only when it is invoiced — <strong>export</strong> via a
              <code> gsti</code> line, <strong>domestic</strong> via <code>gst.Sale_Noc</code>. Dispatch-only
              vouchers ("planned" / "ready for export") do not close a PO. Historical shipments from the Excel
              seed are always preserved.
            </p>
            <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
              <button type="button" className="omh-btn" data-testid="recon-preview-btn"
                disabled={busy} onClick={() => run(true)}
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 18px",
                         border: "1px solid var(--omh-border-strong)", background: "white", borderRadius: 6,
                         fontWeight: 600, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}>
                <Eye size={16} weight="bold" /> {busy && mode === "preview" ? "Previewing…" : "Preview (dry run)"}
              </button>
              <button type="button" data-testid="recon-run-btn"
                disabled={busy} onClick={() => run(false)}
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 20px",
                         border: "none", background: "var(--omh-primary)", color: "white", borderRadius: 6,
                         fontWeight: 700, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}>
                <ArrowsClockwise size={16} weight="bold" /> {busy && mode === "apply" ? "Reconciling…" : "Reconcile now"}
              </button>
              {last?.at && (
                <span className="muted" style={{ fontSize: 11.5, alignSelf: "center" }} data-testid="recon-last-at">
                  Last reconciled: {fmtDateTime(last.at)}
                </span>
              )}
            </div>
          </div>
        </div>

        {view && (
          <>
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }} data-testid="recon-summary">
              {result?.dry_run && (
                <div style={{ width: "100%", fontSize: 12, fontWeight: 700, color: "#B45309" }} data-testid="recon-dryrun-flag">
                  PREVIEW ONLY — no changes were saved.
                </div>
              )}
              <Stat label="POs Scanned" value={fmtNum(view.open_scanned ?? view.scanned)} accent="#64748B" />
              <Stat label="Updated" value={fmtNum(view.updated)} accent="#1D4ED8" />
              <Stat label="Closed" value={fmtNum(view.closed)} accent="#059669" />
              <Stat label="Partial" value={fmtNum(view.partial)} accent="#F59E0B" />
              {(view.reopened || 0) > 0 && <Stat label="Reopened" value={fmtNum(view.reopened)} accent="#E11D48" />}
            </div>

            <div className="jde-panel" data-testid="recon-results-panel">
              <div className="jde-panel-header">
                <div className="jde-panel-title">
                  {result?.dry_run ? "POs that would change" : "POs changed in last run"}
                </div>
              </div>
              <div className="jde-panel-body" style={{ padding: 0 }}>
                <table className="jde-grid">
                  <thead>
                    <tr>
                      <th>Customer PO</th>
                      <th>Customer</th>
                      <th style={{ textAlign: "right" }}>Ordered</th>
                      <th style={{ textAlign: "right" }}>Open (before)</th>
                      <th style={{ textAlign: "right" }}>Open (after)</th>
                      <th style={{ textAlign: "right" }}>Shipped</th>
                      <th style={{ textAlign: "center" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(view.rows || []).map((r, i) => {
                      const st = STATUS_STYLE[r.status] || STATUS_STYLE.open;
                      const Icon = r.status === "closed" ? CheckCircle : (r.new_open > r.old_open ? ArrowUUpLeft : WarningCircle);
                      return (
                        <tr key={`${r.ms_pono}-${i}`} data-testid={`recon-row-${r.ms_pono}`}>
                          <td className="text-mono" style={{ fontWeight: 700 }}>{r.ms_custpo || "—"}</td>
                          <td>{r.customer_name || "—"}</td>
                          <td className="text-mono" style={{ textAlign: "right" }}>{fmtNum(r.ordered)}</td>
                          <td className="text-mono" style={{ textAlign: "right", color: "#94A3B8" }}>{fmtNum(r.old_open)}</td>
                          <td className="text-mono" style={{ textAlign: "right", fontWeight: 700,
                                color: r.new_open <= 0 ? "#059669" : "inherit" }}>{fmtNum(r.new_open)}</td>
                          <td className="text-mono" style={{ textAlign: "right" }}>{fmtNum(r.shipped)}</td>
                          <td style={{ textAlign: "center" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: st.bg,
                                           color: st.fg, padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
                              <Icon size={12} weight="bold" /> {r.status.replace("_", " ")}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                    {(view.rows || []).length === 0 && (
                      <tr><td colSpan={7} className="muted" style={{ padding: 18, textAlign: "center" }} data-testid="recon-empty">
                        Everything is already in sync with the ERP — no POs to update.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
